// Settings-internal TrialBanner — drives the in-page banner at the top of
// Abonnement (US-190). Shares variant logic with the global app-shell
// TrialBanner via the `deriveTrialBannerState` helper from the DS.
//
// Differences from the shell variant:
//   - Renders a richer description (which plan, what price, etc.).
//   - Always shown when `trialing`, even at 7 days left.

import { useT } from '@barel/i18n';
import { TrialBanner as DsTrialBanner, deriveTrialBannerState } from '@barel/ui';

import type { BillingResponse } from '@barel/api-client/react';

export interface SettingsTrialBannerProps {
  data: BillingResponse | undefined;
  onCancelTrial: () => void;
  onUpgrade: () => void;
}

export function SettingsTrialBanner({ data, onCancelTrial, onUpgrade }: SettingsTrialBannerProps) {
  const t = useT('billing');
  const sub = data?.subscription;
  const trial = data?.trial;
  if (!sub || !trial) return null;
  if (trial.status !== 'trialing') return null;
  const daysLeft = trial.trialDaysLeft ?? 0;
  const state = deriveTrialBannerState(daysLeft);
  const upgradeLabel =
    state === 'expired' ? t('trial.upgradeLabelExpired') : t('trial.upgradeLabel');
  return (
    <div data-region="settings-trial-banner">
      <DsTrialBanner
        daysLeft={daysLeft}
        title={t('trial.bannerTitle')}
        label={t('trial.labelNormal', { daysLeft })}
        labelUrgent={t('trial.labelUrgent', { daysLeft })}
        labelEndingToday={t('trial.labelEndingToday')}
        labelExpired={t('trial.labelExpired')}
        cancelLabel={t('trial.cancelLabel')}
        upgradeLabel={upgradeLabel}
        onCancel={onCancelTrial}
        onUpgrade={onUpgrade}
      />
    </div>
  );
}
